import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import BreadCrumb from "../components/BreadCrumb";
import CourseLeft from "../components/CourseDetail/CourseLeft";
import CourseRight from "../components/CourseDetail/CourseRight";
import packageJson from "../../package.json";
import { CartContext } from "../context/CartContext";

const BASE_URL = packageJson.apiUrl;

// ===============================
// BookingForm Component (Basic Info Only)
// ===============================
const BookingForm = ({ course, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const { id } = useParams();
  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    try {
      setLoading(true);
      setStatus("");

      // ✅ Save Basic Info
      const res = await fetch(`${BASE_URL}/inhouse/save-basic-info`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.get("name"),
          email: formData.get("email"),
          address: formData.get("address"),
          postal_code: formData.get("postal_code"),
          notes: formData.get("notes"),
          company_name: formData.get("company_name"),
          course_id: course.id,
          training_date: formData.get("training_date"),
          no_of_users: formData.get("no_of_users"),
        }),
      });

      const data = await res.json();
      if (!data.success) throw new Error("Failed to save booking");

      // ✅ Close modal + redirect if needed
      onClose();
    
      // Save token in sessionStorage for next steps
      sessionStorage.setItem("bookingToken", data.token_unique_key);

      // Redirect to questions page
     navigate(`/booking/questions/${course.slug || course.id}`);

    } catch (err) {
      console.error("Booking error:", err);
      setStatus("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleBookingSubmit}>
      <div className="mb-3">
        <label className="form-label">Your Name</label>
        <input type="text" className="form-control" name="name" required />
      </div>
      <div className="mb-3">
        <label className="form-label">Email</label>
        <input type="email" className="form-control" name="email" required />
      </div>
      <div className="mb-3">
        <label className="form-label">Address</label>
        <input type="text" className="form-control" name="address" required />
      </div>
      <div className="mb-3">
        <label className="form-label">Postal Code</label>
        <input type="text" className="form-control" name="postal_code" required />
      </div>
      <div className="mb-3">
        <label className="form-label">Company Name</label>
        <input type="text" className="form-control" name="company_name" />
      </div>
      <div className="mb-3">
        <label className="form-label">Notes</label>
        <textarea className="form-control" name="notes"></textarea>
      </div>
      <div className="mb-3">
        <label className="form-label">No of Users</label>
        <input type="number" className="form-control" name="no_of_users" min="1" defaultValue="1" />
      </div>
      <div className="mb-3">
        <label className="form-label">Joining Date</label>
        <input type="date" className="form-control" name="training_date" required />
      </div>

      <button type="submit" className="btn btn-green" disabled={loading}>
        {loading ? "Saving..." : "Book Now"}
      </button>

      {status && <p className="text-danger mt-2">{status}</p>}
    </form>
  );
};

// ===============================
// Main CourseDetail Component
// ===============================
const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [seo, setSeo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [faqData, setFaqData] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showBooking, setShowBooking] = useState(false);
  const { addToCart } = useContext(CartContext);

  const [descriptiveQuestions, setDescriptiveQuestions] = useState([]);

 useEffect(() => {
  const fetchCourse = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/course/${id}`);

      // Flexible response handling
      let courseData = null;
      if (res.data.course) {
        courseData = res.data.course;
      } else if (res.data.data) {
        courseData = res.data.data;
      } else if (res.data.id) {
        courseData = res.data; // direct course object
      }

      setCourse(courseData);
      setSeo(res.data.seo || {});
      console.log("Fetched course:", courseData);
    } catch (error) {
      console.error("Error fetching course:", error);
    } finally {
      setLoading(false);
    }
  };
  fetchCourse();
}, [id]);


  // ✅ fetch FAQ
  useEffect(() => {
    const fetchFaq = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/courses/${id}/faq`);
        const faqList = res.data.faqs || res.data.data || [];
        setFaqData(faqList);
        console.log("Fetched FAQ:", faqList);
      } catch (error) {
        console.error("Error fetching FAQ:", error);
      }
    };
    fetchFaq();
  }, [id]);
 // ✅ define handleBuyNow
  const handleBuyNow = () => {
    if (course) {
      addToCart({
        id: course.id,
		image: course.image,
        name: course.name,
        price: course.price,
      });
      navigate("/cart");
    }
  };
  // ✅ fetch descriptive Q&A (dynamic slug)
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        if (course?.slug) {
          const res = await axios.get(
            `https://app.endorselearning.co.uk/api/course/descriptive/${course.slug}`
          );
          if (res.data.success) {
            setDescriptiveQuestions(res.data.questions_answers || []);
            console.log("Fetched descriptive Qs:", res.data.questions_answers);
          }
        }
      } catch (error) {
        console.error("Error fetching descriptive questions:", error);
      }
    };
    fetchQuestions();
  }, [course?.slug]);

  if (loading) return <p>Loading...</p>;

  if (!course) return <p>No course found.</p>;

  return (
    <>
      <Helmet>
        <title>{seo?.title || course?.name}</title>
      </Helmet>

      <BreadCrumb currentPage={course?.name} />

      {/* Course Title & Buy Buttons */}
      <section className="course-title-sec">
        <div className="container">
          <div className="cr-top-pd">
            <div className="row">
              <div className="col-lg-8">
                <h1 className="course-title">{course?.name}</h1>
                <p
                  className="course-short"
                  dangerouslySetInnerHTML={{
                    __html:
                      course?.short_description.length > 100
                        ? course?.short_description.slice(0, 100) + "..."
                        : course?.short_description,
                  }}
                />
              </div>
              <div className="col-lg-4">
                <div className="price-box">
                  <div className="row">
                    <div className="col-lg-6 col-6 my-auto">
                      <p className="cr-price mb-0">£{course?.price}</p>
                    </div>
                    <div className="col-lg-6 col-6 my-auto text-end product ">
                                      {course.course_for === "online" ? ( <button onClick={handleBuyNow} className="btn btn-green"> Enroll Now </button> ) : (course.course_for === "inhouse" || course.course_for === "descriptive") ? ( <button onClick={() => setShowBooking(true)} className="btn btn-green"> Book Now </button> ) : null}

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-space course-detail">
        <div className="container">
          <div className="course-left">
            <div className="row">
              <div className="col-lg-8">
                 <CourseLeft course={course} faqData={faqData} activeIndex={activeIndex} setActiveIndex={setActiveIndex} />
     
              </div>
              <div className="col-lg-4">
               <CourseRight course={course} />
              </div>
            </div>

            <div className="container">
              <div className="row">

                <div className="col-lg-8">
                <div className="cr-card mt-4">
          <div className="cr-card-head">
            <h3 className="cr-title">Frequently Asked Questions</h3>
          </div>
          <div className="cr-card-body">
            <div className="accordion" id="accordionExample">
              {faqData.length > 0 ? (
                faqData.map((faq, index) => (
                  <div className="accordion-item" key={faq.id}>
                    <h2 className="accordion-header" id={`heading${faq.id}`}>
                      <button
                        className="accordion-button"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#collapse${faq.id}`}
                        aria-expanded={activeIndex === index ? "true" : "false"}
                        aria-controls={`collapse${faq.id}`}
                        onClick={() => setActiveIndex(index)}
                      >
                        {faq.question}
                      </button>
                    </h2>
                    <div
                      id={`collapse${faq.id}`}
                      className={`accordion-collapse collapse ${
                        activeIndex === index ? "show" : ""
                      }`}
                      aria-labelledby={`heading${faq.id}`}
                      data-bs-parent="#accordionExample"
                    >
                      <div className="accordion-body">
                        <p>{faq.anwser}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p>Loading FAQs...</p>
              )}
            </div>
          </div>
        </div>
                </div>
              </div>
      </div>
          </div>
        </div>
      </section>
{/* Booking Modal */}

{showBooking && (
  <div className="modal fade show d-block" tabIndex="-1" role="dialog">
    <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">Book {course.title}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setShowBooking(false)}
          ></button>
        </div>
        <div className="modal-body">
          <BookingForm
            course={course}
            onClose={() => setShowBooking(false)}
          />
        </div>
      </div>
    </div>
  </div>
)}



      {/* Rest */}
     
    </>
  );
};

export default CourseDetail;
